import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Icon from './Icon';

// Single-select searchable dropdown for forms. Reuses the .multi-select-* panel
// styling from the Documents filter bar (MultiSelectFilter) so the type-to-find
// UX is consistent across the app — but picks ONE value and closes on select.
//
// options: [{ id, label }]. value is the selected id (or ''). onChange(id).
//
// KEYBOARD: the options are <div role="option">, which is what the ARIA listbox
// pattern asks for — but a div takes no focus, so without the handlers below
// there is NO keyboard path to a selection at all. That mattered here more than
// usual: this control gates "add an ingredient", the core action of עץ מוצר, so
// a mouse-only picker made the whole feature mouse-only.
//
// The active option is tracked by INDEX and announced with aria-activedescendant
// rather than by moving DOM focus: focus stays in the search input so typing
// keeps filtering, which is the behaviour a combobox promises. That is also why
// the input is the combobox element and the panel is its popup — a listbox may
// only contain options, so the search field cannot live inside it.
export default function SearchSelect({ value, options, onChange, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const reactId = useId();
  const listId = `ss-list-${reactId}`;
  const optId = (i) => `ss-opt-${reactId}-${i}`;

  const selected = options.find(o => String(o.id) === String(value));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => String(o.label).toLowerCase().includes(q));
  }, [options, query]);

  // Close and hand focus back to the trigger, so Escape doesn't drop the user
  // at the top of the page — the same contract Modal keeps.
  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) { setQuery(''); return undefined; }
    setActiveIdx(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // A filter keystroke invalidates the highlight — index 3 of the old list is
  // not index 3 of the new one, and leaving it put would arm Enter on a row the
  // user can no longer see.
  useEffect(() => { setActiveIdx(0); }, [query]);

  // Keep the highlighted option in view when arrowing past the panel's edge.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`#${CSS.escape(optId(activeIdx))}`);
    // Guarded rather than called outright: scrollIntoView is not implemented in
    // jsdom, and keeping the highlight visible is a nicety — it must never be
    // the thing that throws inside a render effect.
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIdx, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (o) => { onChange(o.id); close(); };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!filtered.length) return;
      setActiveIdx(i => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        return (next + filtered.length) % filtered.length;
      });
      return;
    }
    if (e.key === 'Home') { e.preventDefault(); setActiveIdx(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActiveIdx(Math.max(0, filtered.length - 1)); return; }
    if (e.key === 'Enter') {
      // Always swallow Enter while the popup is open: this control lives inside
      // a <form>, and letting it bubble would SUBMIT the form instead of
      // choosing the option the user is looking at.
      e.preventDefault();
      const o = filtered[activeIdx];
      if (o) pick(o);
      return;
    }
    if (e.key === 'Tab') close(false);
  };

  return (
    <div className="multi-select" style={{ display: 'block', width: '100%', position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        className="form-control"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={(e) => {
          // Down-arrow opens from the closed trigger — the standard combobox
          // affordance, so a keyboard user need not guess that Enter opens it.
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); setOpen(true); }
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        style={{
          textAlign: 'right', cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span style={{ color: selected ? 'inherit' : 'var(--ink-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : (placeholder || 'בחר…')}
        </span>
        <span style={{ color: 'var(--ink-muted)', flexShrink: 0 }}><Icon name="chevron" size={14} /></span>
      </button>

      {open && (
        <>
          <div className="menu-backdrop" onClick={() => close(false)} />
          <div className="multi-select-panel" style={{ left: 0, right: 0, maxWidth: 'none' }}>
            <input
              ref={inputRef}
              className="multi-select-search"
              placeholder="חיפוש…"
              aria-label="חיפוש"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={filtered.length ? optId(activeIdx) : undefined}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <div className="multi-select-list" id={listId} role="listbox" ref={listRef}>
              {filtered.length === 0 ? (
                <div className="multi-select-empty">{options.length === 0 ? 'אין אפשרויות' : 'לא נמצאו תוצאות'}</div>
              ) : (
                filtered.map((o, i) => {
                  const isSel = String(o.id) === String(value);
                  return (
                    <div
                      key={o.id}
                      id={optId(i)}
                      role="option"
                      aria-selected={isSel}
                      className={`multi-select-option${isSel ? ' is-checked' : ''}${i === activeIdx ? ' is-active' : ''}`}
                      // Pointer highlight follows the mouse so the keyboard and
                      // mouse never disagree about which row Enter would take.
                      onMouseMove={() => setActiveIdx(i)}
                      onClick={() => pick(o)}
                    >
                      <span>{o.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
